import { addDays, startOfDay, startOfWeek } from 'date-fns';

/** The date ranges above the denials table, as the design lists them. */
export type DenialsRange = 'thisWeek' | 'lastWeek' | 'days30' | 'days60' | 'all';

export interface IDenialsRangeOption {
    range: DenialsRange;
    /** On the control. */
    label: string;
    /** In running text — "7 groups · official CARC text · last 30 days". */
    phrase: string;
}

export const DENIALS_RANGES: ReadonlyArray<IDenialsRangeOption> = [
    { range: 'thisWeek', label: 'This week', phrase: 'this week' },
    { range: 'lastWeek', label: 'Last week', phrase: 'last week' },
    { range: 'days30', label: '30 days', phrase: 'last 30 days' },
    { range: 'days60', label: '60 days', phrase: 'last 60 days' },
    { range: 'all', label: 'All', phrase: 'all time' },
];

export const DEFAULT_DENIALS_RANGE: DenialsRange = 'days30';

/** "All" still needs a start: the report takes one. Earlier than any remit on the platform. */
export const DENIALS_ALL_TIME_START = new Date(2000, 0, 1);

export const denialsRangeOption = (range: DenialsRange): IDenialsRangeOption =>
    DENIALS_RANGES.find(option => option.range === range) || DENIALS_RANGES[2];

/**
 * The window to ask the adjustment report for, as `[start, end)`.
 *
 * The report counts remits by the date they were created (measured against production remits, 2026-09-16). Whether a
 * remit created ON the end date counts could not be settled — none of the codes checked had one — so every range that
 * includes today ends tomorrow, which covers today either way. "Last week" is the half-open week that ends where this
 * week starts. Weeks start on Sunday, the US calendar the practices bill on.
 */
export const denialsReportWindow = (range: DenialsRange, now: Date): { start: Date; end: Date } => {
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    switch (range) {
        case 'thisWeek':
            return { start: startOfWeek(today), end: tomorrow };
        case 'lastWeek': {
            const thisWeek = startOfWeek(today);
            return { start: addDays(thisWeek, -7), end: thisWeek };
        }
        case 'days60':
            return { start: addDays(today, -60), end: tomorrow };
        case 'all':
            return { start: DENIALS_ALL_TIME_START, end: tomorrow };
        case 'days30':
        default:
            return { start: addDays(today, -30), end: tomorrow };
    }
};
